/**
 * One-shot handoff for "build a workflow from a prompt": a surface stashes
 * build instructions keyed by workflow id, then navigates to the builder, which
 * consumes them once on mount. Carried in sessionStorage (not the URL) and
 * cleared on consume. See docs/workflow-ai-chat/README.md ("The seed").
 */

import type {WorkflowBuildPurpose} from "@/services/wingman";

export type WorkflowBuildReturnTo = "gailgpt" | "conversation";

export interface WorkflowBuildSeed {
  /** Natural-language description of the workflow to build. */
  prompt: string;
  /**
   * Where to send the user once the build completes. `"gailgpt"` returns to a
   * fresh Gail chat pre-seeded to call the new tool. `"conversation"` returns
   * to the originating chat so account setup can continue.
   */
  returnTo?: WorkflowBuildReturnTo;
  /** Required when `returnTo` is `"conversation"`. */
  returnConversationId?: string;
  /**
   * When true, the builder publishes the workflow after the build finishes.
   * Tool builds also sync + attach. After-call builds publish only.
   */
  autoPublish?: boolean;
  /**
   * The GailGPT assistant that started a tool build. After-call builds omit this.
   */
  originAssistantId?: string;
  /** after_call skips tool exposure and auto-starts the builder. */
  purpose?: WorkflowBuildPurpose;
}

const SEED_KEY_PREFIX = "workflow-build-seed-";

const AFTER_CALL_BUILD_INSTRUCTIONS = [
  "This is an after-call automation, not a callable GailGPT tool.",
  "Do NOT call expose_as_tool.",
  "Start node: webhook.isEnabled true and metadata.gailWebhookEvents = [\"call_processed\"].",
  "Send email (or the specified notification) using fields from the call payload.",
  "Call set_workflow_details with a clear name and description.",
  "Do all of this in this turn.",
].join(" ");

const TOOL_BUILD_INSTRUCTIONS =
  "When the workflow is built, expose it as a callable tool: call " +
  "`set_workflow_details` to set a clear, action-oriented name and a " +
  "description, and call `expose_as_tool` with an `inputSchema` describing " +
  "the inputs this workflow needs (so an agent knows what arguments to " +
  "pass). Reference those inputs in the graph via `node.start.payload.<field>`. " +
  "The description is what a calling AI agent reads to decide WHEN to invoke " +
  "this tool, so write it as the trigger condition — describe the situation/" +
  "request that should cause it to be called and what it returns (e.g. \"Use " +
  "this when the user asks to look up a company's registration details on " +
  "sunbiz; returns the extracted business data\"), not just what the steps do. " +
  "Do all of this in this turn.";

export function workflowBuildSeedKey(workflowId: string): string {
  return `${SEED_KEY_PREFIX}${workflowId}`;
}

export function buildAutomatedChatPrompt(seed: WorkflowBuildSeed): string {
  if (!seed.autoPublish) return seed.prompt;
  if (seed.purpose === "after_call") {
    return `${seed.prompt}\n\n${AFTER_CALL_BUILD_INSTRUCTIONS}`;
  }
  return `${seed.prompt}\n\n${TOOL_BUILD_INSTRUCTIONS}`;
}

/** Translation function shape shared by `react-i18next` and the tests' stubs. */
export type TranslateFn = (key: string, options?: Record<string, unknown>) => string;

/** Translation key (in the `ask` namespace) of the after-call continuation message. */
export const AFTER_CALL_RETURN_INPUT_KEY = "workspace.buildWorkflow.returnMessage";

/**
 * The message sent back to the assistant, as the operator, once an after-call
 * workflow is published. It appears in the conversation as a user turn, so it
 * follows the operator's UI language (`t` must come from the `ask` namespace).
 */
export function buildAfterCallReturnInput(
  t: TranslateFn,
  published: { name: string; id: string }
): string {
  return t(AFTER_CALL_RETURN_INPUT_KEY, { name: published.name, id: published.id });
}

export function writeWorkflowBuildSeed(
  workflowId: string,
  seed: WorkflowBuildSeed
): void {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.setItem(
      workflowBuildSeedKey(workflowId),
      JSON.stringify(seed)
    );
  } catch {
    // sessionStorage may be unavailable (private mode, quota). The build
    // simply won't auto-start; the user can still drive the chat manually.
  }
}

export function readWorkflowBuildSeed(
  workflowId: string
): WorkflowBuildSeed | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.sessionStorage.getItem(workflowBuildSeedKey(workflowId));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<WorkflowBuildSeed>;
    if (!parsed || typeof parsed.prompt !== "string" || !parsed.prompt.trim()) {
      return null;
    }
    return {
      prompt: parsed.prompt,
      ...(parsed.returnTo === "gailgpt" || parsed.returnTo === "conversation"
        ? {returnTo: parsed.returnTo}
        : {}),
      ...(typeof parsed.returnConversationId === "string" &&
      parsed.returnConversationId
        ? {returnConversationId: parsed.returnConversationId}
        : {}),
      ...(parsed.autoPublish ? {autoPublish: true} : {}),
      ...(typeof parsed.originAssistantId === "string" && parsed.originAssistantId
        ? {originAssistantId: parsed.originAssistantId}
        : {}),
      ...(parsed.purpose === "after_call" || parsed.purpose === "tool"
        ? {purpose: parsed.purpose}
        : {}),
    };
  } catch {
    return null;
  }
}

export function clearWorkflowBuildSeed(workflowId: string): void {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.removeItem(workflowBuildSeedKey(workflowId));
  } catch {
    // ignore
  }
}

/**
 * Cache of consumed seeds, so consumption survives React Strict-Mode remounts
 * (which reset component state but not module scope). A genuine refresh resets
 * this module and sessionStorage is already cleared, so the build won't replay.
 */
const consumedSeeds = new Map<string, WorkflowBuildSeed | null>();

/**
 * Idempotently reads (and clears) the one-shot seed. First call reads
 * sessionStorage and caches it; later calls return the cached value.
 */
export function consumeWorkflowBuildSeed(
  workflowId: string
): WorkflowBuildSeed | null {
  if (consumedSeeds.has(workflowId)) {
    return consumedSeeds.get(workflowId) ?? null;
  }
  const seed = readWorkflowBuildSeed(workflowId);
  consumedSeeds.set(workflowId, seed);
  // Clear so a hard refresh doesn't replay; the cache covers remounts.
  clearWorkflowBuildSeed(workflowId);
  return seed;
}

/**
 * Forces later reads to `null` for this session. The consume cache otherwise
 * keeps returning the seed (needed for Strict-Mode remounts), which would loop
 * a finished build on any re-mount. Call once the build has been handled.
 */
export function markWorkflowBuildSeedConsumed(workflowId: string): void {
  consumedSeeds.set(workflowId, null);
}
